import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { WidgetService } from '../../main/widgets/widget.service';
import { IProcessList, Process, PROCESSFIXEDFIELDS } from '../models/process';
import { ProcessType } from '../models/process-type';
import { Subject } from '../models/subject';
import { Template } from '../models/template';
import { BaseRestService } from './base-rest.service';
import { LookupService } from './lookup.service';
import { ProcessTypeService } from './process-type.service';
import { TemplateService } from './template.service';
import { UserService } from './user.service';
import { User } from '../models/user';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { pick } from 'lodash-es';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class ProcessService extends BaseRestService<Process, IProcessList> implements OnDestroy {

  constructor(http: HttpClient,
    public processTypeService: ProcessTypeService,
    private lookupService: LookupService,
    private templateService: TemplateService,
    private userService: UserService,
    private widgetService: WidgetService,
    protected router: Router) {
    super(ProcessService, http, 'api/processes', 'processes', router);
  }

  ngOnDestroy(): void { }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {

    // tslint:disable-next-line:no-inferrable-types
    let processTypeName: string = '';
    this.routeParams = route.params;
    if (this.routeParams.processtype) {
      processTypeName = this.routeParams.processtype;
    }
    if (this.routeParams.id) {
      if ((!this.routeParams.processtype) && (!((this.routeParams.id === 'new') || (/\d/.test(this.routeParams.id))))) {
        processTypeName = this.routeParams.id;
      }
    }
    return super.resolve(route, state, processTypeName);
  }

  private getProcessTypeName(processType: string | ProcessType): string {
    processType = processType || '';
    return (typeof processType === 'string') ? processType : processType.name;
  }

  // for use where it is necessary to lookup processes for a lookup list
  getAllByProcessType(processType: string | ProcessType, urlParams?: string): Observable<Process[]> {
    return this.getAllData(encodeURIComponent(this.getProcessTypeName(processType)), urlParams).pipe(take(1));
  }

  getSubjectProcesses(subject: Subject, processType?: string | ProcessType): Observable<Process[]> {
    let getAll$;

    if (!subject.uid)
      return of([]);

    // if subject and process type specified then take advantage of the temporary caching by fetching all processes linked to the subject...
    if (subject && processType && (processType instanceof ProcessType)) {
      getAll$ = this.getAllData(undefined, `subjectid=${subject.uid}`);
      return getAll$.pipe(map((data: Process[]) => {
        const d = data.filter((x) => x.processTypeId === processType.id);
        return d;
      }));
    }
    else {
      if (subject && !subject.uid)
        return of([]);

      // ... not able to take advantage of the caching so filter by process type and subject as required
      getAll$ = (processType) ? this.getAllByProcessType(processType, `subjectid=${subject.uid}`) : this.getAllData(undefined, `subjectid=${subject.uid}`);
      return getAll$.pipe(map((data: Process[]) => {
        const d = data.filter((x) => x.subjectId === subject.id);
        return d;
      }));
    }
  }

  getSubjectCheckLists(subject: Subject): Observable<Process[]> {

    // if no subject passed then return an empty observable
    if (!subject.uid)
      return of([]);
    
    // let getAll$;
    // if subject and process type specified then take advantage of the temporary caching by fetching all processes linked to the subject...    
    this.clearCache();
    const fetch$ = [this.processTypeService.getAllData().pipe(take(1)), this.getAllData(undefined, `subjectid=${subject.uid}`).pipe(take(1))];
    return forkJoin(fetch$).pipe(map(([processTypes, data]: [ProcessType[], Process[]]) => {
      var checklistRecords: Process[] = [];
      if (data && data.length > 0) {
        data.forEach(i => {
          var pt = processTypes.find(x => x.id == i.processTypeId);
          if (pt && pt.isCheckList == true) {
            var p = new Process(i);
            p.processType = new ProcessType(pt);
            checklistRecords.push(p);
          }
        });
      }
      return checklistRecords;
    }));
  }

  hydrate(item: Process): Observable<Process> {
    const hydrate$ = [];
    // check if we have process data.  If we do then we need to populate the displayValue property
    if (item.processData) {
      item.processData.displayValues = {};
      if (item.processData.data) {
        // get process type
        hydrate$.push(this.processTypeService.getById(item.processTypeId).pipe(take(1), map((processType) => {

          // iterate over process data fields - the process type gives us the possible fields
          processType.dataFields.forEach((field) => {
            // check if is a custom field in the process data - if it is then check if we need to lookup the display value
            if (PROCESSFIXEDFIELDS.indexOf(field.name.toLowerCase()) < 0) {
              const itemData = item.processData.data[field.id];
              if (itemData !== undefined) {
                let displayValue = itemData;
                if ((/^\d+$/.test(field.dataType)) && (/^\d+$/.test(itemData)) && (!isNaN(itemData))) {
                  displayValue = this.lookupService.getDescription(Number(itemData));
                }
                if (itemData && (field.dataType.toLowerCase() === 'user')) {
                  if ((/^\d+$/.test(itemData)) && (!isNaN(itemData))) {
                    const user = this.userService.getUserById(Number(itemData));
                    if (user) {
                      displayValue = new User(user).fullName;
                    }
                  }
                }
                item.processData.displayValues[field.id] = displayValue;
              }
            }
          });
          return item;
        })));
      }
    }

    if (hydrate$.length > 0) {
      return forkJoin(hydrate$).pipe(take(1), map((_) => {
        return item;
      }));
    }
    else {
      return of(item);
    }
  }

  getDefaultTemplate(templateType: string, processType: ProcessType): Template {
    const widgets = this.widgetService.getAvailableFieldWidgets(templateType, processType.dataFields);
    return this.templateService.createDefaultTemplate(templateType, widgets);
  }
}
